/**
 * Column Presets for Instrument Explorer
 * 
 * Provides pre-configured column visibility sets for different workflow contexts.
 * Users can select presets for common tasks or use custom column configuration.
 */

export type PresetKey = 'general' | 'template-review' | 'contract-definitions' | 'view-archive' | 'custom';

export interface ColumnPreset {
  name: string;
  description: string;
  columns: Set<string>;
  autoFilter?: boolean; // Apply automatic filters for this preset
}

/**
 * Available column presets for different workflow contexts
 */
export const COLUMN_PRESETS: Record<PresetKey, ColumnPreset> = {
  'general': {
    name: 'General',
    description: 'Basic instrument overview for daily trading workflows',
    columns: new Set([
      'symbol',
      'displayName', 
      'assetClass',
      'marketSegment',
      'subAssetClass',
      'status',
      'quoteCurrency'
    ])
  },
  
  'template-review': {
    name: 'Template Review',
    description: 'Review template sync status (filtered to template-linked instruments)',
    columns: new Set([
      'symbol',
      'displayName',
      'instrumentReferenceId', 
      'templateStatus',
      'createdAt',
      'updatedAt'
    ]),
    autoFilter: true // Will filter to instruments with instrumentReferenceId
  },
  
  'contract-definitions': {
    name: 'Contract Definitions',
    description: 'Contract specifications and trading parameters',
    columns: new Set([
      'symbol',
      'displayName',
      'contractSize',
      'contractAsset',
      'incrementSize',
      'incrementType',
      'tickValue', // Contract Value per Increment
      'quoteCurrency'
    ])
  },
  
  'view-archive': {
    name: 'View Archive',
    description: 'View archived instruments only',
    columns: new Set([
      'symbol',
      'displayName',
      'assetClass',
      'status',
      'source',
      'createdAt',
      'updatedAt'
    ]),
    autoFilter: true // Will filter to archived instruments only
  },
  
  'custom': {
    name: 'Custom',
    description: 'User-defined column selection',
    columns: new Set() // Empty set - will be populated by user selections
  }
} as const;

/**
 * Get all available preset keys except 'custom'
 */
export function getAvailablePresets(): PresetKey[] {
  return Object.keys(COLUMN_PRESETS).filter(key => key !== 'custom') as PresetKey[];
}

/**
 * Get preset configuration by key
 */
export function getPresetByKey(key: PresetKey): ColumnPreset {
  return COLUMN_PRESETS[key];
}

/**
 * Apply a preset to current column visibility state
 */
export function applyPreset(presetKey: PresetKey, currentColumns?: Set<string>): {
  visibleColumns: Set<string>;
  selectedPreset: PresetKey;
  shouldFilter?: boolean;
} {
  if (presetKey === 'custom') {
    return {
      visibleColumns: currentColumns || new Set(),
      selectedPreset: 'custom'
    };
  }
  
  const preset = COLUMN_PRESETS[presetKey];
  return {
    visibleColumns: new Set(preset.columns),
    selectedPreset: presetKey,
    ...(preset.autoFilter && { shouldFilter: preset.autoFilter })
  };
}

/**
 * Determine which preset matches the current column selection
 * Returns 'custom' if no exact preset match is found
 */
export function detectCurrentPreset(visibleColumns: Set<string>): PresetKey {
  for (const [key, preset] of Object.entries(COLUMN_PRESETS)) {
    if (key === 'custom') continue;
    
    // Check if sets are equal
    if (preset.columns.size === visibleColumns.size &&
        [...preset.columns].every(col => visibleColumns.has(col))) {
      return key as PresetKey;
    }
  }
  
  return 'custom';
}

/**
 * Get preset options for Select component
 */
export function getPresetOptions(): Array<{key: PresetKey; label: string; description: string}> {
  return Object.entries(COLUMN_PRESETS).map(([key, preset]) => ({
    key: key as PresetKey,
    label: preset.name,
    description: preset.description
  }));
}